import { useState, useEffect, useCallback } from 'react';

/**
 * Pure label helper — maps an update state + version to display text and tone.
 *
 * @param {'idle'|'available'|'downloading'|'ready'} state
 * @param {string} version  Current or incoming version string.
 * @returns {{ text: string, version: string, tone: 'ok'|'busy'|'action' }}
 */
export function updateLabel(state, version) {
  switch (state) {
    case 'idle':
      return { text: 'Up to date', version, tone: 'ok' };
    case 'available':
      return { text: 'Update available', version, tone: 'action' };
    case 'downloading':
      return { text: 'Downloading update…', version, tone: 'busy' };
    case 'ready':
      return { text: 'Update ready — Restart', version, tone: 'action' };
    default:
      return { text: 'Up to date', version, tone: 'ok' };
  }
}

/**
 * useUpdateStatus — subscribes to Electron auto-update IPC and exposes a
 * normalised state machine for the sidebar update row.
 *
 * In a browser context (no window.electronAPI) state stays 'idle' and version
 * stays '' — the UpdateStatusRow renders null in that case.
 *
 * @returns {{
 *   version: string,
 *   state: 'idle'|'available'|'downloading'|'ready',
 *   progress: number,
 *   install: () => void,
 * }}
 */
export function useUpdateStatus() {
  const [version, setVersion] = useState('');
  const [state, setState] = useState('idle');
  const [progress, setProgress] = useState(0);

  const install = useCallback(() => {
    if (typeof window !== 'undefined' && window.electronAPI?.installUpdate) {
      window.electronAPI.installUpdate();
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI) return;

    let cancelled = false;

    // Seed version and cached update state on mount so we never miss an event
    // that fired before this component mounted.
    if (typeof window.electronAPI.getVersion === 'function') {
      window.electronAPI.getVersion().then((v) => {
        if (!cancelled) setVersion(v);
      }).catch(() => {});
    }

    if (typeof window.electronAPI.getUpdateState === 'function') {
      window.electronAPI.getUpdateState().then((cached) => {
        if (cancelled || !cached || cached.kind === 'idle') return;
        if (cached.kind === 'available') setState('available');
        else if (cached.kind === 'downloading') {
          setProgress(cached.progress || 0);
          setState('downloading');
        } else if (cached.kind === 'ready') {
          setState('ready');
        }
      }).catch(() => {});
    }

    const cleanups = [];

    if (typeof window.electronAPI.onUpdateAvailable === 'function') {
      cleanups.push(
        window.electronAPI.onUpdateAvailable(() => {
          if (!cancelled) setState('available');
        }),
      );
    }

    if (typeof window.electronAPI.onUpdateProgress === 'function') {
      cleanups.push(
        window.electronAPI.onUpdateProgress((p) => {
          if (!cancelled) {
            setProgress(Math.round(p.percent));
            setState('downloading');
          }
        }),
      );
    }

    if (typeof window.electronAPI.onUpdateDownloaded === 'function') {
      cleanups.push(
        window.electronAPI.onUpdateDownloaded(() => {
          if (!cancelled) setState('ready');
        }),
      );
    }

    return () => {
      cancelled = true;
      cleanups.forEach((fn) => typeof fn === 'function' && fn());
    };
  }, []);

  return { version, state, progress, install };
}
